/**
 * Visual tuning for Waves mode rendering. All values are in CSS px
 * unless otherwise noted; the renderer applies the layout's
 * playfield-to-canvas scale before drawing so identical visuals
 * appear at any window size.
 */

/** Approach-velocity scaling: obstacles further than this many beats away aren't drawn (offscreen + slop). */
export const OBSTACLE_DRAW_BEATS_AHEAD = 6;

/** Visual obstacle thickness (CSS px) — the body of a spike or tunnel pillar. */
export const OBSTACLE_VISUAL_THICKNESS = 18;

/** Border / glow thickness around obstacles in CSS px. */
export const OBSTACLE_GLOW_THICKNESS = 4;

/** Player trail sample count (one per frame, fades by index). */
export const PLAYER_TRAIL_LENGTH = 24;

/** Shorter trail on low-tier devices — halves segment draw cost. */
export const PLAYER_TRAIL_LENGTH_LOW_TIER = 12;

/** Parallax star count when decorations are enabled (high tier). */
export const STAR_COUNT = 60;

/**
 * Parallax star drift speed as a fraction of canvas width per beat.
 * Beat-locked (follows audio clock + loop speed ramp) so the sense of
 * forward motion accelerates alongside the music as the player climbs
 * loops. Tuned to read as "the ship is moving fast through the field"
 * rather than a slow ambient drift.
 */
export const STAR_DRIFT_FRAC_PER_BEAT = 0.1;

/** Cap active shockwave rings — bounds per-frame stroke + shadow work. */
export const SHOCKWAVE_POOL_MAX = 32;

/** Glow ring radius multiplier around the player when pressing (hold = boost). */
export const PLAYER_HOLD_GLOW_RADIUS_MUL = 2.4;

/** Star/cup milestone banner colours, lifted from the Pulse renderer. */
export const MILESTONE_COLOUR = '#ffd84d';

/** Death effect colour (red — same vocabulary as Pulse/Guitar). */
export const DEATH_COLOUR = '#ff3b5c';

/** Verdict colours for floating score pops. */
export const VERDICT_COLOURS = {
  perfect: '#3bff9d',
  good: '#16e0ff',
  miss: '#ff3b5c',
} as const;

/** Floating-text lifetimes in seconds (verdict pops + milestone banners). */
export const FLOATING_TEXT_LIFE = 0.55;
export const FLOATING_TEXT_LIFE_BIG = 1.1;
export const SCORE_POP_LIFE = 0.75;

/**
 * Vertical anchor for big celebration banners (combo + star/cup
 * milestones), as a fraction of canvas height. Sits high as a
 * top-centre headline so it clears the mid-screen lane where upcoming
 * obstacles scroll in — the player can still read incoming spikes
 * while the banner plays. Horizontally centred (canvasWidth / 2), so
 * it never collides with the top-left score HUD or top-right
 * leaderboard.
 */
export const BIG_BANNER_Y_FRAC = 0.16;

/**
 * Verdict floating-text anchor offsets, in CSS px, relative to the
 * player. The text sits LEFT and ABOVE the ship so it doesn't overlay
 * the player or block the right-side obstacle approach lane.
 */
export const VERDICT_TEXT_X_OFFSET = 90;
export const VERDICT_TEXT_Y_OFFSET = 32;

/**
 * Minimum left margin for verdict text inside the playfield. Allows
 * for centred-text half-width (~55px for "PERFECT!" at 23px font) plus
 * the ±11px X-jitter applied by `spawnFloatingText` so the longest
 * verdict glyphs never clip past the left edge on narrow viewports.
 */
export const VERDICT_TEXT_LEFT_MARGIN = 70;

/** Top-left score HUD position (CSS px from canvas top-left corner). */
export const SCORE_HUD_X = 24;
export const SCORE_HUD_Y = 18;

/**
 * Combo banner thresholds — when the player passes one of these
 * obstacle counts without dying, a celebratory banner appears.
 * Mirrors the Guitar/Piano vocabulary so combo milestones feel
 * cross-mode consistent.
 */
export const COMBO_MILESTONES = [10, 25, 50, 100] as const;

/** Combo at which a single miss becomes a "big" combo break. */
export const BIG_COMBO_BREAK_THRESHOLD = 25;

/**
 * Star particle burst counts per milestone tier (1 = star_1, 2 =
 * star_2, etc.). Same scaling Pulse uses so the visual reward feels
 * familiar across modes.
 */
export const MILESTONE_BURST_COUNT = [40, 60, 90, 120, 150, 200] as const;

/** Default key label rendered in the HUD's "Hold X" hint. */
export const DEFAULT_KEY_LABEL = 'SPACE';

/**
 * Playfield-unit vertical clearance below which an obstacle pass is
 * shown as a "close call" — bright accent flourish + "CLOSE!" pop.
 * Pure cosmetic: no scoring or replay-determinism impact.
 */
export const CLOSE_CALL_CLEARANCE = 14;

/** Hot-pink accent used for close-call FX, in keeping with the combo-milestone palette. */
export const CLOSE_CALL_COLOUR = '#ff2bd6';

/** Beat-sync pulse strength for corridor / player glow. */
export const BEAT_PULSE_STRENGTH = 0.38;

/** Hit-stop durations (ms) — render freeze only, engine/audio unaffected. */
export const HIT_STOP_MS_PASS = 32;
export const HIT_STOP_MS_CLOSE = 48;
export const HIT_STOP_MS_DEATH = 90;
export const HIT_STOP_MAX_MS = 120;

/** Obstacle approach glow ramps over this many beats before the player. */
export const OBSTACLE_APPROACH_PULSE_BEATS = 1.2;

/**
 * Saw spin speed in radians per beat. Beat-locked (not wall-clock) so a
 * blade's rotation reads as driven by the song and stays consistent
 * across the loop speed ramp. Purely cosmetic — collision is the
 * 1-D sum-of-radii test at the saw's beat, indifferent to rotation.
 */
export const SAW_SPIN_RAD_PER_BEAT = 2.4;

/** Number of teeth on a saw blade. */
export const SAW_TEETH = 10;

/** Tooth valley radius as a fraction of the blade (collision) radius. */
export const SAW_VALLEY_FRAC = 0.78;

/** Forward rake of each tooth tip (fraction of a tooth's angular pitch). */
export const SAW_TOOTH_RAKE_FRAC = 0.34;

/** Central hub radius as a fraction of the blade radius. */
export const SAW_HUB_FRAC = 0.4;
